import { zodResolver } from '@hookform/resolvers/zod';
import { addMonths, type BudgetLine, type SetBudgetRequest } from '@familywallet/shared';
import { useMutation } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';
import { type FieldError, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { FormError } from '../components/FormError';
import { Row } from '../components/FormRow';
import { PageHeader } from '../components/PageHeader';
import { OverBadge, RemainingText, StatusBar } from '../components/PlanningCards';
import { Button, buttonVariants } from '../components/ui/button';
import { Card, ListGroup, SectionLabel } from '../components/ui/card';
import { api } from '../lib/api';
import { useCan } from '../lib/auth';
import { useBudgets } from '../lib/ledger';
import { useInvalidateLedger } from '../lib/ledger-mutations';
import { formatAmount, groupAmountInput } from '../lib/money';
import { type BudgetFormValues, budgetFormSchema } from '../lib/planning-forms';
import { CategoryChip, CategoryPicker } from './AddPage';

function useMonthName() {
  const { i18n } = useTranslation();
  return (month: string) =>
    new Intl.DateTimeFormat(i18n.language, {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(`${month}-01T00:00:00Z`));
}

/** One budget line: name, spent of amount, the bar and what is left (or how much over). */
function BudgetRow({
  line,
  currency,
  editable,
}: {
  line: BudgetLine;
  currency: string;
  editable: boolean;
}) {
  const { t, i18n } = useTranslation();
  const body = (
    <>
      <span className="flex items-center gap-2">
        <span className="min-w-0 flex-1 truncate text-[15px] font-medium">{line.name}</span>
        {line.status === 'over' && <OverBadge label={t('budgets.overBadge')} />}
        {editable && (
          <ChevronRight aria-hidden="true" className="size-5 shrink-0 text-muted rtl:rotate-180" />
        )}
      </span>
      {line.amount !== null && line.remaining !== null ? (
        <>
          <span className="flex items-baseline justify-between gap-3">
            <span dir="ltr" className="text-[13px] text-muted tabular-nums">
              {t('budgets.spentOf', {
                spent: formatAmount(line.spent, currency, i18n.language),
                amount: formatAmount(line.amount, currency, i18n.language),
              })}
            </span>
            <RemainingText remaining={line.remaining} currency={currency} overKey="budgets.over" />
          </span>
          <StatusBar
            percent={line.percent}
            status={line.status ?? 'normal'}
            label={line.name ?? ''}
          />
          {line.status === 'warning' && (
            <span className="text-xs text-expense">{t('budgets.warning')}</span>
          )}
        </>
      ) : (
        <span dir="ltr" className="text-[13px] text-muted tabular-nums">
          {t('budgets.spentOnly', { spent: formatAmount(line.spent, currency, i18n.language) })}
        </span>
      )}
    </>
  );
  const className = 'flex min-h-16 flex-col gap-2 px-4 py-3';
  return (
    <li>
      {editable ? (
        <Link
          to={`/budgets/set?category=${line.categoryId}`}
          className={`${className} hover:bg-surface-2 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent`}
        >
          {body}
        </Link>
      ) : (
        <div className={className}>{body}</div>
      )}
    </li>
  );
}

/** Budgets: the household cap, category budgets, and categories spent on without a budget. */
export function BudgetsPage() {
  const { t, i18n } = useTranslation();
  const [params, setParams] = useSearchParams();
  const monthName = useMonthName();
  const requested = params.get('month') ?? undefined;
  const budgets = useBudgets(requested);
  if (!budgets.data) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title={t('budgets.title')} back="/" />
        {budgets.isError ? (
          <FormError error={budgets.error} />
        ) : (
          <p role="status">{t('app.loading')}</p>
        )}
      </div>
    );
  }
  const data = budgets.data;
  const current = data.month === data.currentMonth;
  const editable = data.canManage && current;
  const { overall } = data;
  const withBudget = data.categories.filter((c) => c.amount !== null);
  const without = data.categories.filter((c) => c.amount === null);
  const go = (month: string) =>
    setParams(month === data.currentMonth ? {} : { month }, { replace: true });
  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={t('budgets.title')}
        back="/"
        action={
          editable && (
            <Link to="/budgets/set" className={buttonVariants({ variant: 'small' })}>
              {t('budgets.add')}
            </Link>
          )
        }
      />
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          aria-label={t('budgets.previousMonth')}
          onClick={() => go(addMonths(data.month, -1))}
          className="flex size-11 items-center justify-center rounded-xl text-accent focus-visible:outline-2 focus-visible:outline-accent"
        >
          <ChevronLeft aria-hidden="true" className="size-5 rtl:rotate-180" />
        </button>
        <h2 className="text-[15px] font-semibold" aria-live="polite">
          {monthName(data.month)}
        </h2>
        <button
          type="button"
          aria-label={t('budgets.nextMonth')}
          disabled={current}
          onClick={() => go(addMonths(data.month, 1))}
          className="flex size-11 items-center justify-center rounded-xl text-accent disabled:text-muted disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-accent"
        >
          <ChevronRight aria-hidden="true" className="size-5 rtl:rotate-180" />
        </button>
      </div>

      <Card className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <h2 className="flex-1 text-[15px] font-semibold">{t('budgets.overall')}</h2>
          {overall.status === 'over' && <OverBadge label={t('budgets.overBadge')} />}
          {editable && (
            <Link
              to="/budgets/set?category=overall"
              className="inline-flex min-h-11 items-center rounded-xl px-1 text-[13px] font-semibold text-accent focus-visible:outline-2 focus-visible:outline-accent"
            >
              {overall.amount === null ? t('budgets.set') : t('budgets.edit')}
            </Link>
          )}
        </div>
        {overall.amount !== null && overall.remaining !== null ? (
          <>
            <span dir="ltr" className="text-2xl font-semibold tabular-nums">
              {formatAmount(overall.spent, data.currency, i18n.language)}
            </span>
            <div className="flex items-baseline justify-between gap-3">
              <span dir="ltr" className="text-[13px] text-muted tabular-nums">
                {t('budgets.spentOf', {
                  spent: formatAmount(overall.spent, data.currency, i18n.language),
                  amount: formatAmount(overall.amount, data.currency, i18n.language),
                })}
              </span>
              <RemainingText
                remaining={overall.remaining}
                currency={data.currency}
                overKey="budgets.over"
              />
            </div>
            <StatusBar
              percent={overall.percent}
              status={overall.status ?? 'normal'}
              label={t('budgets.overall')}
            />
            {overall.status === 'warning' && (
              <span className="text-xs text-expense">{t('budgets.warning')}</span>
            )}
          </>
        ) : (
          <span dir="ltr" className="text-[13px] text-muted tabular-nums">
            {t('budgets.noBudget')} ·{' '}
            {t('budgets.spentOnly', {
              spent: formatAmount(overall.spent, data.currency, i18n.language),
            })}
          </span>
        )}
        <span className="text-xs text-muted">{t('budgets.overallHint')}</span>
      </Card>

      {withBudget.length === 0 && overall.amount === null && (
        <Card className="flex flex-col gap-4">
          <p className="text-muted">{t('budgets.empty')}</p>
          {editable && (
            <Link to="/budgets/set?category=overall" className={buttonVariants()}>
              {t('budgets.set')}
            </Link>
          )}
        </Card>
      )}

      {withBudget.length > 0 && (
        <section className="flex flex-col gap-2">
          <SectionLabel>{t('budgets.categories')}</SectionLabel>
          <ListGroup>
            {withBudget.map((line) => (
              <BudgetRow
                key={line.categoryId}
                line={line}
                currency={data.currency}
                editable={editable}
              />
            ))}
          </ListGroup>
        </section>
      )}
      {without.length > 0 && (
        <section className="flex flex-col gap-2">
          <SectionLabel>{t('budgets.unbudgeted')}</SectionLabel>
          <ListGroup>
            {without.map((line) => (
              <BudgetRow
                key={line.categoryId}
                line={line}
                currency={data.currency}
                editable={editable}
              />
            ))}
          </ListGroup>
        </section>
      )}
      {!current && <p className="px-1 text-xs text-muted">{t('budgets.pastMonth')}</p>}
      {!data.canManage && <p className="px-1 text-xs text-muted">{t('budgets.readOnly')}</p>}
      <p className="px-1 text-xs text-muted">
        {t('budgets.countsHint', { currency: data.currency })}
      </p>
    </div>
  );
}

/** Set or change one budget (/budgets/set?category=overall|<id>), from this month on. */
export function BudgetFormPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const canManage = useCan('budgets.manage');
  const budgets = useBudgets();
  if (!budgets.data) {
    if (budgets.isError) return <FormError error={budgets.error} />;
    return <p role="status">{t('app.loading')}</p>;
  }
  if (!canManage) {
    return (
      <Card role="alert">
        <p>{t('budgets.readOnly')}</p>
      </Card>
    );
  }
  const category = params.get('category') ?? '';
  const line =
    category === 'overall'
      ? budgets.data.overall
      : budgets.data.categories.find((c) => c.categoryId === category);
  return (
    <BudgetForm
      key={category}
      category={category}
      amount={line?.amount ?? null}
      currency={budgets.data.currency}
    />
  );
}

function BudgetForm({
  category,
  amount,
  currency,
}: {
  category: string;
  amount: string | null;
  currency: string;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const invalidate = useInvalidateLedger();
  const [picking, setPicking] = useState(false);
  const resolver = useMemo(() => zodResolver(budgetFormSchema(currency)), [currency]);
  const form = useForm<BudgetFormValues, unknown, SetBudgetRequest>({
    resolver,
    defaultValues: {
      categoryId: category,
      amount: amount ? groupAmountInput(amount, currency) : '',
    },
  });
  const v = useWatch({ control: form.control }) as BudgetFormValues;
  const errors = form.formState.errors;
  const errorText = (error: FieldError | undefined) =>
    error?.message ? t(error.message, { currency }) : null;
  const save = useMutation({
    mutationFn: (body: SetBudgetRequest) => api.budgets.set(body),
    onSuccess: async () => {
      await invalidate();
      navigate('/budgets');
    },
  });
  const isOverall = v.categoryId === 'overall';
  return (
    <form
      noValidate
      className="flex flex-col gap-4"
      onSubmit={form.handleSubmit((body) => save.mutate(body))}
    >
      <PageHeader
        title={isOverall ? t('budgets.overall') : t('budgets.formTitle')}
        back="/budgets"
      />
      <ListGroup>
        {!isOverall && (
          <Row label={t('budgets.category')} error={errorText(errors.categoryId)}>
            <CategoryChip kind="expense" id={v.categoryId} onClick={() => setPicking(true)} />
          </Row>
        )}
        <Row
          label={t('budgets.amount', { currency })}
          htmlFor="budget-amount"
          error={errorText(errors.amount)}
        >
          <input
            id="budget-amount"
            inputMode={currency === 'LBP' ? 'text' : 'decimal'}
            autoComplete="off"
            dir="ltr"
            placeholder="0.00"
            aria-invalid={errors.amount ? true : undefined}
            className="min-h-11 w-32 rounded-full bg-surface-2 px-3 text-end text-[15px] font-medium tabular-nums focus-visible:outline-2 focus-visible:outline-accent"
            {...form.register('amount')}
          />
        </Row>
      </ListGroup>
      {isOverall && <p className="px-1 text-xs text-muted">{t('budgets.overallHint')}</p>}
      <p className="px-1 text-xs text-muted">{t('budgets.fromThisMonth')}</p>
      <FormError error={save.error} />
      <Button type="submit" disabled={save.isPending}>
        {save.isPending ? t('common.saving') : t('budgets.save')}
      </Button>
      {amount !== null && (
        <Button
          variant="danger"
          disabled={save.isPending}
          onClick={() => save.mutate({ categoryId: isOverall ? null : v.categoryId, amount: '0' })}
        >
          {t('budgets.remove')}
        </Button>
      )}
      {picking && (
        <CategoryPicker
          kind="expense"
          selected={v.categoryId}
          onPick={(id) => {
            form.setValue('categoryId', id);
            setPicking(false);
          }}
          onClose={() => setPicking(false)}
        />
      )}
    </form>
  );
}
